"use client";

import { ArrowRight, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Program = {
  badge: string;
  title: string;
  accent: string;
  description: string;
  bullets: string;
  bullets2: string;
  bullets3: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const programs: Program[] = [{
  badge: "Neuromodulator",
  title: "Refined",
  accent: "Tox",
  description: "Experience a tailored approach to wrinkle reduction. We focus on subtle, natural-looking results that smooth expression lines while preserving your unique ability to convey emotion—ensuring you look refreshed, not frozen.",
  bullets: "Precision placement for natural movement",
  bullets2: "Customized dosing for your facial anatomy",
  bullets3: "Target forehead creases, smile lines, and heavy brows",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/trusted-botox-injector-san-antonio.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/trusted-botox-injector-san-antonio.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/trusted-botox-injector-san-antonio.webp-wvc-sizes",
  alt: "A serene, professional clinical setting for Botox treatment"
}, {
  badge: "Dermal Fillers",
  title: "Facial",
  accent: "Balancing",
  description: "Restore volume and refine your proportions with our expert filler techniques. Whether enhancing cheekbones, defining the jawline, or softening nasolabial folds, we prioritize symmetry and structural harmony.",
  bullets: "Strategic volume restoration for youthful contours",
  bullets2: "Advanced techniques for facial symmetry",
  bullets3: "High-quality, long-lasting dermal products",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/filler_balancing_results.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/filler_balancing_results.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/filler_balancing_results.png-wvc-sizes",
  alt: "A close-up of a balanced, natural facial profile"
}, {
  badge: "Prevention",
  title: "Proactive",
  accent: "Care",
  description: "Early intervention is the secret to long-term skin health. We use neuromodulators and light filler to prevent deep-set wrinkles before they form, maintaining your skin's elasticity and smoothness for years to come.",
  bullets: "Preventative neuromodulators for younger patients",
  bullets2: "Early volume support to maintain skin structure",
  bullets3: "Evidence-based approach to aging gracefully",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/preventative-botox-neuromodulators.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/preventative-botox-neuromodulators.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/preventative-botox-neuromodulators.webp-wvc-sizes",
  alt: "A professional consultation focusing on preventative skin health"
}, {
  badge: "Concierge",
  title: "Private",
  accent: "Experience",
  description: "Enjoy the luxury of professional aesthetic care in the comfort of your own space. Our concierge service eliminates the stress of traffic and waiting rooms, providing a discreet, personalized, and seamless experience.",
  bullets: "Private, in-home or office aesthetic sessions",
  bullets2: "No waiting rooms, no travel, total privacy",
  bullets3: "Dedicated time with Dr. Ojeda, FNP",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/concierge_luxury_service.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/concierge_luxury_service.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/concierge_luxury_service.png-wvc-sizes",
  alt: "A luxury, private setting for a concierge aesthetic appointment"
}];
function ProgramRow({
  program,
  index
}: {
  program: Program;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  const bullets = [program.bullets, program.bullets2, program.bullets3];
  return <div ref={ref} data-index={index} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mt-16 md:mt-20 first:mt-0">
      <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateX(0)" : "translateX(16px)",
      transition: "opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1)"
    }}>
        <div className="overflow-hidden rounded-lg border border-border" style={{
        contain: "layout"
      }}>
          <img src={program.image} data-wvc-srcset={program.srcset} data-wvc-sizes={program.sizes} alt={program.alt} loading="lazy" decoding="async" className="w-full h-full object-cover aspect-[4/5] transition-transform ease-out hover:scale-[1.02]" />
        </div>
      </div>

      <div className={`lg:col-span-6 ${isReversed ? "lg:order-1" : "lg:order-2"}`} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(16px)",
      transition: "opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1) 120ms, transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1) 120ms"
    }}>
        <Badge variant="secondary" className="bg-primary text-primary-foreground border-transparent rounded-lg px-3 py-1 text-xs font-medium tracking-wider uppercase font-default">
          {program.badge}
        </Badge>

        <h2 className="mt-5 font-default font-medium text-foreground leading-[1.12] tracking-tight text-[clamp(2rem,4.2vw,3rem)]">
          {program.title}{" "}
          <em className="italic font-serif font-normal text-foreground">{program.accent}</em>
        </h2>

        <p className="mt-6 md:mt-8 font-default text-muted-foreground text-[clamp(1.125rem,1.4vw,1.3125rem)] leading-[1.65] font-normal">
          {program.description}
        </p>

        <ul className="mt-8 space-y-4">
          {bullets.map((bullet, i) => <li data-index={i} key={i} className="flex items-start gap-3">
              <Check className="mt-1 shrink-0 text-primary" strokeWidth={1.5} size={20} />
              <span className="font-default text-foreground text-base leading-[1.7]">
                {bullet}
              </span>
            </li>)}
        </ul>

        <div className="mt-8">
          <Link to="/ExperiencePage" className="inline-flex items-center gap-2 font-default font-medium text-foreground border-b border-foreground/40 pb-1 hover:text-primary hover:border-primary transition-colors duration-[250ms]">
            <span>Learn more about this service</span>
            <ArrowRight size={18} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>;
}
export default function FacialBalancing() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="18993" id="the-art-of-facial-balancing" className="bg-background pt-20 md:pt-28 pb-16 md:pb-24 light">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={introRef} className="max-w-[760px] mx-auto text-center mb-12 md:mb-16" style={{
        opacity: introVisible ? 1 : 0,
        transform: introVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 900ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          <span className="inline-block font-default text-[0.8125rem] font-bold tracking-[0.2em] uppercase text-primary">
            Services
          </span>

          <h1 className="mt-4 font-default font-medium text-foreground leading-[1.06] tracking-tight text-[clamp(2.5rem,6.2vw,4.75rem)]">
            Refined{" "}
            <span className="relative inline-block">
              <em className="italic font-serif font-normal">balancing</em>
              <svg className="absolute left-0 right-0 -bottom-2 w-full" viewBox="0 0 120 8" fill="none" preserveAspectRatio="none" aria-hidden="true">
                <path d="M2 5 Q 20 1, 40 4 T 80 4 T 118 3" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" fill="none" style={{
                strokeDasharray: 140,
                strokeDashoffset: introVisible ? 0 : 140,
                transition: "stroke-dashoffset 1200ms ease-out 600ms"
              }} />
              </svg>
            </span>{" "}
            for you.
          </h1>

          <p className="mt-6 md:mt-8 font-default text-muted-foreground text-[clamp(1.125rem,1.4vw,1.3125rem)] leading-[1.65] font-normal">At Ideal Image Aesthetics and Wellness, LLC, we believe in enhancing your natural beauty through precise, expert-led facial balancing. Our concierge approach ensures your journey to confidence is as seamless and refined as the results we deliver.</p>
        </div>

        <div>
          {programs.map((program, i) => <ProgramRow key={i} program={program} index={i} />)}
        </div>
      </div>
    </section>;
}